'use client'

import {useMemo, useState} from 'react'

import {closeNotif, startNotif} from '@/components/DynamicForm/util/notifier'
import {Button, Checkbox} from '@/components/UiElements'
import linkUserToCompany from '@/services/UserService/linkUserToCompany'
import {Companies} from '@/types/companies'
import {User} from '@/types/user'
import {getErrorMessage} from '@/utils/errorMessages'

type LinkCompaniesModalProps = {
    user: User
    companies: Companies[]
    linkedCompanies: Companies[]
    onClose: () => void
    onCompaniesLinked: (companies: Companies[]) => void
}

const LinkCompaniesModal = ({
    user,
    companies,
    linkedCompanies,
    onClose,
    onCompaniesLinked,
}: LinkCompaniesModalProps) => {
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set())
    const [isSaving, setIsSaving] = useState(false)

    const linkedCompanyIds = useMemo(
        () => new Set(linkedCompanies.map(company => company.id)),
        [linkedCompanies],
    )
    const selectedCompanies = useMemo(
        () => companies.filter(company => selectedCompanyIds.has(company.id)),
        [companies, selectedCompanyIds],
    )

    const handleCompanyChange = (companyId: string, checked: boolean) => {
        setSelectedCompanyIds(current => {
            const next = new Set(current)
            if (checked) {
                next.add(companyId)
            } else {
                next.delete(companyId)
            }
            return next
        })
    }

    const handleLinkCompanies = async () => {
        if (selectedCompanies.length === 0 || isSaving) return

        setIsSaving(true)
        const notificationId = startNotif(
            selectedCompanies.length === 1 ? 'Linking company...' : 'Linking companies...',
        )
        const linkedDuringRequest: Companies[] = []

        try {
            for (const company of selectedCompanies) {
                await linkUserToCompany(user.email, company.id)
                linkedDuringRequest.push(company)
            }

            onCompaniesLinked(linkedDuringRequest)
            closeNotif(
                notificationId,
                linkedDuringRequest.length === 1
                    ? 'Company linked successfully.'
                    : 'Companies linked successfully.',
            )
            onClose()
        } catch (error) {
            if (linkedDuringRequest.length > 0) {
                onCompaniesLinked(linkedDuringRequest)
            }
            closeNotif(
                notificationId,
                getErrorMessage(error, 'Failed to link companies.'),
                {type: 'error'},
            )
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="flex w-[560px] max-w-[calc(100vw-80px)] flex-col gap-xl">
            {companies.length > 0 && (
                <ul className="divide-y divide-gray-200 border-y border-gray-200">
                    {companies.map(company => {
                        const isLinked = linkedCompanyIds.has(company.id)
                        const description = company.store_name && company.store_name !== company.name
                            ? company.store_name
                            : undefined

                        return (
                            <li key={company.id} className="px-md">
                                <Checkbox
                                    label={company.name || company.store_name || company.id}
                                    description={description}
                                    defaultChecked={isLinked || selectedCompanyIds.has(company.id)}
                                    disabled={isLinked || isSaving}
                                    onChange={checked => handleCompanyChange(company.id, checked)}
                                    className={() => 'min-h-[56px] w-full py-sm'}
                                />
                            </li>
                        )
                    })}
                </ul>
            )}

            {companies.length === 0 && (
                <div className="flex min-h-[100px] items-center justify-center rounded-md bg-gray-50 p-xl text-text-md text-gray-400">
                    No companies are available.
                </div>
            )}

            <div className="flex justify-end gap-md">
                <Button size="lg" type="alt" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    size="lg"
                    onClick={handleLinkCompanies}
                    disabled={isSaving || selectedCompanies.length === 0}
                >
                    Link
                </Button>
            </div>
        </div>
    )
}

export default LinkCompaniesModal

